import React, { useEffect, useState, createRef } from 'react'
import Helmet from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory } from 'react-router-dom'
import { Card } from 'react-bootstrap'
import moment from 'moment'

import { createPackage } from '../api/packageBuilder'

import Layout from '../components/Layout'

import NameInput from '../components/PackageBuilder/components/NameInput'
import SelectEvents from '../components/PackageBuilder/SelectEvents'
import SelectTargetingMode from '../components/PackageBuilder/SelectTargetingMode'
import BuildResults from '../components/PackageBuilder/BuildResults'
import ProgressBar from '../components/PackageBuilder/components/ProgressBar'
import LoadingSpinner, { SpinnerSize } from '../components/Shared/LoadingSpinner'
import Button, { TransparentBgButton } from '../components/Shared/Button'

import smoothscroll from 'smoothscroll-polyfill'
smoothscroll.polyfill()


/**
 * Page for a users to create a new Package. 
 * creates a payload for a put request executed in final step and redirects to /package-builder/results/<packageId>
 * @Tags( page, packageBuilder, create, layout, logged-users, api )
 * @SuggestedTags( packageBuilder )
 * @Endpoints( /package-builder/create )
 * @ApiLogic( ../api/package-builder/create/ )
 */

const PackageBuilder = () => {
    const { t } = useTranslation()

    const nameInputRef = createRef()
    const buttonRef = createRef()
    const history = useHistory()

    const [loading, setLoading] = useState(false)
    const [selectedEvents, setSelectedEvents] = useState([])
    const [selectedTargetMode, setSelectedTargetMode] = useState('')
    const [accordionOpen, setAccordionOpen] = useState(true)
    const [currentStep, setCurrentStep] = useState(1)
    const [currentCompletedStep, setCurrentCompletedStep] = useState(1)
    const [packageName, setPackageName] = useState(`Package-${moment().format('DD-MM-YYYY:hh')}`)


    const [completed, setCompleted] = useState(false)
    const [redirectId, setRedirectId] = useState(null)

    useEffect(() => {
        // check if a user has previous results in local storage and show them if they have
        const parsedLocalState = JSON.parse(localStorage.getItem('packageBuilderState'))

        if (!parsedLocalState) {
            const initialState = { listItems: [], targetMode: {}, variationsNumber: 3, currentStep: 1, currentCompletedStep: 1, name: `Package-${moment().format('DD-MM-YYYY:hh')}` }
            localStorage.setItem('packageBuilderState', JSON.stringify(initialState))
        } else {

            if (parsedLocalState.listItems && parsedLocalState.listItems.length) {
                setSelectedEvents(parsedLocalState.listItems)
            }

            setPackageName(parsedLocalState.name ?? `Package-${moment().format('DD-MM-YYYY:hh')}`)

            setCurrentStep(Number(parsedLocalState.currentCompletedStep))

            setCurrentCompletedStep(Number(parsedLocalState.currentCompletedStep))

            setSelectedTargetMode(parsedLocalState.targetMode)
        }
        return () => {
            // cd unmount
        }
    }, [])

    // useEffect(() => {
    //     // if list exists, scroll to bottom
    //     if (!completed && selectedEvents.length > 4) {
    //         buttonRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    //     }
    // }, [buttonRef, completed])

    const handlePackageName = (name) => {

        setPackageName(name)

        const parsedLocalState = JSON.parse(localStorage.getItem('packageBuilderState'))

        parsedLocalState.name = name

        localStorage.setItem('packageBuilderState', JSON.stringify(parsedLocalState))
    }

    const updateStep = (newStep) => {
        const parsedLocalState = JSON.parse(localStorage.getItem('packageBuilderState'))

        setCurrentStep(newStep)
        parsedLocalState.currentStep = newStep
        localStorage.setItem('packageBuilderState', JSON.stringify(parsedLocalState))
    }

    const moveStepForward = () => {
        const parsedLocalState = JSON.parse(localStorage.getItem('packageBuilderState'))

        updateStep(currentStep < 3 ? currentStep + 1 : currentStep)
        setCurrentCompletedStep(currentCompletedStep < 3 ? currentCompletedStep + 1 : 3)

        parsedLocalState.currentCompletedStep = currentCompletedStep < 3 ? currentCompletedStep + 1 : 3
        localStorage.setItem('packageBuilderState', JSON.stringify(parsedLocalState))
    }

    const moveStepBackward = () => {
        if (currentStep === 1) {
            const initialState = { listItems: [], variationsNumber: 3, currentStep: 1, currentCompletedStep: 1, name: `Package-${moment().format('DD-MM-YYYY:hh')}` }
            localStorage.setItem('packageBuilderState', JSON.stringify(initialState))
            history.goBack()
            return
        }

        updateStep(currentStep > 1 ? currentStep - 1 : 1)
        setCurrentCompletedStep(currentCompletedStep > 1 ? currentCompletedStep - 1 : 1)
        const parsedLocalState = JSON.parse(localStorage.getItem('packageBuilderState'))
        parsedLocalState.currentCompletedStep = currentCompletedStep > 1 ? currentCompletedStep - 1 : 1
        localStorage.setItem('packageBuilderState', JSON.stringify(parsedLocalState))
    }

    const submitFormAndStartOver = async () => {
        // simulated API call TODO: remove
        setLoading(true)
        try {
            const eventIds = selectedEvents.map(event => event.id)
            const res = await createPackage(
                {
                    'num_packages': 3,
                    'n_top_events': 3,
                    'event_ids': eventIds,
                    'package_title': packageName,
                    'targeting_mode': selectedTargetMode

                }
            )

            if (res.success) {
                const initialState = { listItems: [], variationsNumber: 3, currentStep: 1, currentCompletedStep: 1, name: `Package-${moment().format('DD-MM-YYYY:hh')}` }
                localStorage.setItem('packageBuilderState', JSON.stringify(initialState))

                const { id: packageId } = res.payload

                setRedirectId(packageId)
                setCompleted(true)
            }
        } catch (error) {
            console.error({ error })
        } finally {
            setLoading(false)
        }
    }

    if (completed) {
        return <Redirect to={`/package-builder/results/${redirectId}`} />
    }

    const isNextDisabled = (currentStep === 1 && selectedEvents.length === 0) || (currentStep === 2 && !selectedTargetMode)

    return (
        <Layout
            loggedIn
            title={t('PackageBuilder.title')} mobileTopBar={{ back: true, centerTitle: t('PackageBuilder.title') }}
        >
            <Helmet bodyAttributes={{ class: 'package-builder mobile-bg-white' }}
            />
            <main data-cy="package-builder-create-screen" id='package-builder-create-page' className='pb-4'>
                <div style={{ minHeight: '80vh', paddingBottom: '6em' }} className="container-fluid hide-scroll-bar">

                    <NameInput
                        packageName={packageName}
                        handlePackageName={handlePackageName}
                        nameInputRef={nameInputRef}
                        setPackageName={setPackageName}
                    />
                    <div className='mt-5'>
                        <ProgressBar
                            currentStep={currentStep}
                            setCurrentStep={setCurrentStep}
                            currentCompletedStep={currentCompletedStep}
                        />
                    </div>

                    {loading ? <LoadingSpinner portalBackground={false} size={SpinnerSize.LARGE} /> : null}

                    {!loading ?
                        <Card className='package-builder-page_outer-wrapper mt-2'>
                            {currentStep === 1 ?
                                <SelectEvents
                                    selectedEvents={selectedEvents}
                                    setSelectedEvents={setSelectedEvents}
                                    accordionOpen={accordionOpen}
                                    setAccordionOpen={setAccordionOpen}
                                    updateStep={updateStep}
                                    buttonRef={buttonRef}
                                />
                                : null
                            }
                            {currentStep === 2 ?
                                <SelectTargetingMode
                                    selectedEventsNumber={selectedEvents.length}
                                    updateStep={updateStep}
                                    setTargetMode={setSelectedTargetMode}
                                    targetMode={selectedTargetMode}
                                />

                                : null
                            }
                            {currentStep === 3 ?
                                <BuildResults
                                    selectedEvents={selectedEvents}
                                    packageName={packageName}
                                    setCurrentStep={setCurrentStep}
                                    isMerged={false}
                                    targetMode={selectedTargetMode}
                                />

                                : null
                            }
                        </Card>
                        : null
                    }

                    <div className="package-builder-page_submit-btn" ref={buttonRef}>
                        <TransparentBgButton
                            className="bt"
                            onClick={moveStepBackward}
                            text={t('common.back')}
                            fullWidth={false}
                            color="navy"
                        />
                        {currentStep === 3 && !loading ?
                            <Button cypressId='build-package-button' text={t('PackageBuilder.buildPackage')} onClick={submitFormAndStartOver} disabled={currentStep !== 3 || selectedEvents.length === 0} />
                            :
                            <Button cypressId='next-step-button' text={t('PackageBuilder.nextStep')} disabled={isNextDisabled} onClick={moveStepForward} hidden={currentStep === 3} />
                        }
                    </div>
                </div>
            </main>
        </Layout >

    )
}


export default PackageBuilder